/**
 * Cliente HTTP minimalista para el backend.
 * Wraps de fetch que añaden base URL, credenciales y manejo de errores.
 */

// Por defecto usar mismo origen (frontend y API servidos por Caddy bajo la misma IP/dominio).
// Sólo en desarrollo local stand-alone (Vite dev server) usar localhost:3001.
const API_BASE = window.__CARLOS_OS_API__ ?? '';

class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request(method, path, body) {
  const url = `${API_BASE}${path}`;
  let res;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: body
        ? {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(timeoutForPath(path)),
    });
  } catch (err) {
    // En Safari "Load failed" es genérico — añadimos contexto para debugging.
    const detail = err?.name === 'TimeoutError' ? ('timeout ' + Math.round(timeoutForPath(path)/1000) + 's') :
                   err?.name === 'AbortError' ? 'cancelado' :
                   err?.message || 'red no disponible';
    console.error('[api] fetch FAILED', { method, url, error: detail, raw: err });
    throw new ApiError(`Conexión fallida (${detail}) — ${method} ${path}`, 0, null);
  }

  const contentType = res.headers.get('content-type') || '';
  let data;
  try {
    data = contentType.includes('application/json') ? await res.json() : await res.text();
  } catch (parseErr) {
    console.error('[api] parse response error', { url, status: res.status, parseErr });
    data = null;
  }

  if (!res.ok) {
    throw new ApiError(
      (data && data.message) || `HTTP ${res.status} en ${method} ${path}`,
      res.status,
      data,
    );
  }

  return data;
}


// Algunos endpoints (LLM, sync inicial) requieren más tiempo
function timeoutForPath(path) {
  if (!path) return 15000;
  if (path.includes('/orchestrator/think')) return 90000;
  if (path.includes('/master/ask')) return 60000;
  if (path.includes('/investments/analysis')) return 30000;
  if (path.includes('/portfolio/sync')) return 45000;
  if (path.includes('/properties/sync-ical')) return 30000;
  return 15000;
}

export const apiClient = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  patch: (path, body) => request('PATCH', path, body),
  delete: (path) => request('DELETE', path),
};

export { ApiError };
